import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";

import { useDropzone } from "react-dropzone";
import { showToast } from "../../store/actions";

const useStyles = makeStyles((theme) =>
    createStyles({
        dropzoneContainer: {
            display: "flex",
            flexDirection: "column"
        },
        thumbsContainer: {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap'
        },
        thumb: {
            display: 'inline-flex',
            borderRadius: 2,
            border: '1px solid #eaeaea',
            marginBottom: 8,
            marginRight: 8,
            width: 100,
            height: 100,
            padding: 4,
            boxSizing: 'border-box'
        },
        thumbInner: {
            display: 'flex',
            minWidth: 0,
            overflow: 'hidden'
        },
        thumbImg: {
            display: 'block',
            width: '100%',
            height: '100%'
        }
    })
);

const baseStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out'
};

const focusedStyle = {
    borderColor: '#2196f3'
};

const acceptStyle = {
    borderColor: '#00e676'
};

const rejectStyle = {
    borderColor: '#ff1744'
};

const Dropzone = ({ handleUploadFile }) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const imageFileTypes = {
        "image/jpeg": [".jpeg", ".jpg"],
        "image/png": [".png"]
    };

    const acceptedFileTypes = {
        ...imageFileTypes,
        "application/pdf": [".pdf"],
        "application/eps": [".eps"],
        "application/psd": [".psd"],
        "application/ai": [".ai"]
    };

    const [files, setFiles] = useState([]);

    // Custom File Validator
    const fileValidator = (accFiles) => {
        let uploadedFiles = accFiles;
        let displaySuccessMsg = true;

        if (files.length) {
            const uploadedFileNames = uploadedFiles.map(x => x.name);

            if (uploadedFileNames.length === 1) {
                const existingFile = files.findIndex(file => file.name === uploadedFileNames[0]);
                if (existingFile !== -1) uploadedFiles = [];
            }
            else {
                files.map(file => {
                    // Removing duplicates from uploaded files
                    if (uploadedFileNames.includes(file.name)) {
                        const existingImgFileIndex = uploadedFiles.findIndex(upldFile => upldFile.name === file.name);
                        uploadedFiles.splice(existingImgFileIndex, 1);
                    }
                });
            }

            if (!uploadedFiles.length) displaySuccessMsg = false;
        }

        if (displaySuccessMsg) {
            const finalUploadedFiles = uploadedFiles.map(file => {
                // Create preview only for image file type
                if (Object.keys(imageFileTypes).includes(file.type)) {
                    Object.assign(file, { preview: URL.createObjectURL(file) });
                }
                return file;
            });

            setFiles((prevFiles) => [...prevFiles, ...finalUploadedFiles]);
            dispatch(showToast({ message: "Image(s) uploaded successfully.", type: "success" }));
        }
        else
            dispatch(showToast({ message: "Duplicate image(s) found!", type: "error" }));
    };

    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject
    } = useDropzone({
        accept: acceptedFileTypes,
        onDrop: (acceptedFiles) => { fileValidator(acceptedFiles); }
    });

    const style = useMemo(() => ({
        ...baseStyle,
        ...(isFocused ? focusedStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {})
    }), [
        isFocused,
        isDragAccept,
        isDragReject
    ]);

    useEffect(() => {
        // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
        return () => files.forEach(file => { if (Object.keys(imageFileTypes).includes(file.type)) { URL.revokeObjectURL(file.preview); } });
    }, []);

    useEffect(() => {
        if (files.length) {
            let newUploadedFiles = [];

            files.forEach(file => {
                if (Object.keys(imageFileTypes).includes(file.type)) {
                    // Creating copy of file object, for removing "preview" property from "file" object
                    // Only for image file type
                    let fileObjCopy = new File([file], file.name, {
                        type: file.type,
                        lastModified: file.lastModified,
                        lastModifiedDate: file.lastModifiedDate
                    });

                    // Adding back "path" property from original object "file"
                    Object.assign(fileObjCopy, { path: file.path });

                    newUploadedFiles.push(fileObjCopy);
                }
                else {
                    newUploadedFiles.push(file);
                }
            });

            handleUploadFile(newUploadedFiles);
        }
    }, [files]);

    // Create image preview
    const imgThumbs = files.map(file => {
        // Create preview only for image file type
        if (Object.keys(imageFileTypes).includes(file.type)) {
            return (
                <div className={classes.thumb} key={file.name}>
                    <div className={classes.thumbInner}>
                        <img
                            src={file.preview}
                            className={classes.thumbImg}
                            // Revoke data uri after image is loaded
                            onLoad={() => { URL.revokeObjectURL(file.preview) }}
                        />
                    </div>
                </div>
            );
        }
    });

    const otherFiles = files.map(file => {
        if (!Object.keys(imageFileTypes).includes(file.type)) {
            return (
                <li key={file.name}>
                    {file.name}
                </li>
            );
        }
    });

    return (
        <section className={classes.dropzoneContainer}>
            <div {...getRootProps({ style })}>
                <input {...getInputProps()} />
                <p>Drag 'n' drop some files here, or click to select files</p>
            </div>

            <h4>Image Files</h4>

            <aside className={classes.thumbsContainer}>
                {imgThumbs}
            </aside>

            <h4>Other Files</h4>

            <aside>
                <ul style={{ marginTop: 0 }}>
                    {otherFiles}
                </ul>
            </aside>
        </section>
    );
};

export default Dropzone;